import { useState } from "react";
import { createPortal } from "react-dom";
import { Sun, Moon, Monitor, Palette, X, Check, Sparkles, Layers, Gamepad2, CircleDot, Aperture, LayoutGrid, Bot, AppWindow, Command, Smartphone, Disc, Triangle, Leaf, Rocket, Server, Terminal, PanelsTopLeft, Chrome, Cloud } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useTheme, THEME_SKINS } from "@/lib/theme";

const MODE_OPTIONS = [
  { key: "auto", label: "Auto", icon: Monitor },
  { key: "light", label: "Claro", icon: Sun },
  { key: "dark", label: "Oscuro", icon: Moon },
];

const OPTIONS = [
  { key: "aurora", label: "Aurora", icon: Sun, desc: "Interfaz limpia estilo iOS: blancos puros y azul sistema, en claro u oscuro" },
  { key: "nexus", label: "Nexus", icon: Moon, desc: "Industrial con neón cyan de marca, en claro u oscuro" },
  { key: "talos", label: "Talos OS", icon: Cloud, desc: "OS cloud-native de servidor y seguridad (Sidero Talos): naranja #FF6B1A, estética terminal/servidor, esquinas 2px. En claro u oscuro." },
  { key: "glass", label: "Glass", icon: Sparkles, desc: "Frosted glass estilo macOS/iOS: esquinas redondeadas, blur y vibrancy. Sigue el claro/oscuro del sistema." },
  { key: "hybrid", label: "Híbrido", icon: Layers, desc: "Estilo Zorin/Elementary: esquinas finas, glassmorphism ligero y profundidad. Sigue el sistema." },
  { key: "neon", label: "Neón", icon: Gamepad2, desc: "Estilo Garuda Linux: glossy dark-first con neón cyan y glow intenso. Modo claro sobrio." },
  { key: "ubuntu", label: "Ubuntu", icon: CircleDot, desc: "Yaru 24.04: naranja #E95420 sobre aubergine, esquinas 8px. Sigue el claro/oscuro del sistema." },
  { key: "debian", label: "Debian", icon: Aperture, desc: "Bookworm 12: rojo Debian #D70A53, esquinas finas 4px. Sigue el sistema." },
  { key: "windows11", label: "Windows 11", icon: LayoutGrid, desc: "Fluent/Mica: azul #0078D4, esquinas 8px y blur mica. Sigue el sistema." },
  { key: "android", label: "Android", icon: Bot, desc: "Material 3 (Android 15): violeta #6750A4, esquinas 16px y dynamic color. Sigue el sistema." },
  { key: "windows10", label: "Windows 10", icon: AppWindow, desc: "Fluent plano: azul #0078D4, esquinas 2px. En claro u oscuro." },
  { key: "macos", label: "macOS Sequoia", icon: Command, desc: "Sequoia: púrpura sistema, vibrancy y esquinas 10px. En claro u oscuro." },
  { key: "ios", label: "iOS", icon: Smartphone, desc: "Sistema móvil Apple: índigo #5E5CE6, esquinas 12px y frosted. En claro u oscuro." },
  { key: "fedora", label: "Fedora", icon: Disc, desc: "Azul #3C6EB4, esquinas 8px. En claro u oscuro." },
  { key: "arch", label: "Arch Linux", icon: Triangle, desc: "Minimalista cyan #1793D1, esquinas 0px. En claro u oscuro." },
  { key: "mint", label: "Linux Mint", icon: Leaf, desc: "Verde #87CF3E, esquinas 6px. En claro u oscuro." },
  { key: "pop", label: "Pop!_OS", icon: Rocket, desc: "Teal #48B9C7, esquinas 8px. En claro u oscuro." },
  { key: "suse", label: "openSUSE", icon: Server, desc: "Verde Geeko #73BA25, esquinas 6px. En claro u oscuro." },
  { key: "kali", label: "Kali Linux", icon: Terminal, desc: "Azul dragón #367BF0, dark-first. En claro u oscuro." },
  { key: "elementary", label: "elementary OS", icon: PanelsTopLeft, desc: "Pantheon limpio: acento Grape púrpura, esquinas 4px. En claro u oscuro." },
  { key: "chromeos", label: "Chrome OS", icon: Chrome, desc: "Material Google: acento verde #0F9D58, esquinas 6px. En claro u oscuro." }
];

export default function ThemeToggle({ compact = false, iconClassName }) {
  const { theme, setTheme, mode, setMode, transparency, setTransparency, neon, setNeon } = useTheme();
  const [open, setOpen] = useState(false);
  const current = OPTIONS.find(o => o.key === theme) || OPTIONS[1];
  const CurrentIcon = current.icon;

  return (
    <>
      <button onClick={() => setOpen(true)} title={`Tema: ${current.label}`}
        className={`flex items-center p-1.5 ${iconClassName ?? "text-ink-muted"} hover:text-cyan transition-colors`}>
        <CurrentIcon className="w-4 h-4" />
        {!compact && <span className="hidden sm:inline ml-1.5 text-[0.7rem] font-medium">{current.label}</span>}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-ink/80 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-sm">
            <div role="dialog" aria-modal="true" className="w-full max-h-[calc(100dvh-2rem)] flex flex-col bg-ink-surface border border-cyan/40 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-2.5 sm:px-5 sm:py-4 border-b border-ink-border shrink-0">
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-cyan" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wide">Apariencia</h3>
                </div>
                <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-text p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-2.5 space-y-1.5 sm:p-4 sm:space-y-2 overflow-y-auto flex-1 modal-scroll">
                {OPTIONS.map(o => {
                  const Icon = o.icon;
                  const active = theme === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => { setTheme(o.key); if (!THEME_SKINS.includes(o.key)) setOpen(false); }}
                      className={`w-full flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border text-left transition-colors ${active ? "border-cyan bg-cyan/10 text-cyan" : "border-ink-border text-ink-text hover:border-cyan/50 hover:bg-cyan/5"}`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-xs sm:text-sm uppercase tracking-wide">{o.label}</div>
                        <div className="text-[0.65rem] sm:text-[0.7rem] text-ink-muted truncate leading-tight sm:leading-normal">{o.desc}</div>
                      </div>
                      {active && <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>

              {THEME_SKINS.includes(theme) && (
                <div className="px-2.5 pt-2 pb-0.5 sm:px-4 sm:pt-3 sm:pb-1 shrink-0">
                  <div className="text-[0.6rem] sm:text-[0.65rem] uppercase tracking-wider text-ink-muted mb-1.5 sm:mb-2 px-1">Estilo del tema</div>
                  <div className="grid grid-cols-3 gap-1 border border-ink-border">
                    {MODE_OPTIONS.map(o => {
                      const Icon = o.icon;
                      const active = mode === o.key;
                      return (
                        <button
                          key={o.key}
                          onClick={() => setMode(o.key)}
                          className={`flex flex-col items-center gap-0.5 sm:gap-1 py-1.5 sm:py-2.5 transition-colors ${active ? "bg-cyan text-ink" : "text-ink-muted hover:bg-cyan/10 hover:text-cyan"}`}
                        >
                          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span className="text-[0.6rem] sm:text-[0.65rem] font-display font-bold uppercase tracking-wide">{o.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="px-2.5 pt-2 pb-1 sm:px-4 sm:pt-3 sm:pb-2 shrink-0">
                <div className="flex items-center justify-between gap-3 px-1">
                  <div className="min-w-0">
                    <div className="text-[0.6rem] sm:text-[0.65rem] uppercase tracking-wider text-ink-muted">Efecto Neón</div>
                    <p className="text-[0.58rem] sm:text-[0.62rem] text-ink-muted leading-tight mt-0.5">Glow neón del acento sobre cualquier estilo (auto/claro/oscuro).</p>
                  </div>
                  <Switch checked={neon} onCheckedChange={setNeon} aria-label="Activar efecto neón" />
                </div>
              </div>

              <div className="px-2.5 pt-2 pb-1 sm:px-4 sm:pt-3 sm:pb-2 shrink-0">
                <div className="flex items-center justify-between mb-1.5 sm:mb-2 px-1">
                  <span className="text-[0.6rem] sm:text-[0.65rem] uppercase tracking-wider text-ink-muted">Transparencia (glass)</span>
                  <span className="text-[0.6rem] sm:text-[0.65rem] font-mono text-cyan">{transparency}%</span>
                </div>
                <input
                  type="range" min="0" max="100" step="5" value={transparency}
                  onChange={(e) => setTransparency(e.target.value)}
                  className="w-full accent-cyan h-1.5 cursor-pointer"
                  aria-label="Transparencia de ventanas y widgets"
                />
                <p className="text-[0.58rem] sm:text-[0.62rem] text-ink-muted px-1 mt-1 leading-tight">Aplica efecto glass a modales, widgets, menús y selectores. Conserva la legibilidad.</p>
              </div>

              <div className="px-4 py-2 sm:px-5 sm:py-3 border-t border-ink-border text-[0.6rem] sm:text-[0.65rem] text-ink-muted text-center shrink-0">
                El cambio se aplica al instante en toda la plataforma
              </div>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
}